import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import { type AppFeatures } from '@/modules/config';
import type { Contact } from '@/modules/contact';
import {
  TemplateLogic,
  type ContextVars,
  type MessageIntent,
  type SharingMode,
  type Template,
  type TemplateSet,
} from '@/modules/template';
import { MESSAGE_INTENTS } from '@/modules/template/schema';
import { ALLOWED_DETAIL_FIELD_ID } from '@/modules/template/types';
import { ALLOWED_DETAIL_VARIABLE } from '@/modules/form/schema';
import { TopicLogic } from '@/modules/topic/logic';
import {
  INTENT_DEFINITIONS,
  RECIPIENT_DEFINITIONS,
  type ShareFacts,
  type IntentFacts,
  type RecipientFacts,
  type PrimitiveDefinition,
} from '@/modules/form/lang';
import {
  ComposeLogic,
  EnvironmentLogic,
  FormLogic,
  SlotLogic,
  type AnsweredSlot,
  type AskedSlotIdByLinkedSlotId,
  type Environment,
  type FormFacts,
  type SharingFormSlot,
} from '@/modules/form';
import { defaultShareFormYaml } from '@/modules/topic_garden/defaultShareForms';
import { topicGardenEnabled } from '@/conditional_feature/topic_garden_enabled';
import type { Topic } from '@/modules/topic/types';
import type { ReshareInfoLike, LegStoryInfoLike } from '@/services/meprfo';
import type { AddressingMode, PreviewSegment } from './types';
import { ReshareStoryLogic } from './reshare_story_logic';
import { PreviewLogic, type HypotheticalPeople } from './preview_logic';
import { ShareWizardLogic } from './logic';
import { useReshareStories } from './useReshareStories';

export interface UseShareComposerParams {
  topic: Topic | null;
  /** The runtime config `GET /api/config` supplies. */
  features: AppFeatures;
  sharingMode: SharingMode;
  isUpdateFlow: boolean;
  /** The change-list summary an update's body carries. */
  updateSummary: string;
  /** The leg a reshare passes on, or null outside a reshare (#447). */
  previousLegId: string | null;
  /** The previous leg's story payload, once it loads (#447). */
  reshareInfoForPrevLeg: ReshareInfoLike | null;
  /** The sender's profile name, which signs the leg link. */
  userName: string | undefined;
  /** The name the sender lets travel with a reshared story. */
  allowedName: string | undefined;
  /** The resharer's connection to whoever sent them the leg (garden only). */
  connectionToPrevLeg: string;
  /** The sender's profile pronouns (#506). */
  preferredPronouns: string | undefined;
  /** Who gets which message, from `useShareRecipients`. */
  contactsByIntent: Record<MessageIntent, Contact[]>;
  /** Whether one intent's message reaches exactly one person. */
  intentReachesOnePerson: (intent: MessageIntent) => boolean;
  /** The chosen tone, from `useShareFormChoices`. */
  selectedToneId: string | null;
  setFieldValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  fieldValues: Record<string, string>;
  presentToast: (options: {
    message: string;
    duration: number;
    color: string;
  }) => void;
}

export interface ShareComposer {
  // ── the form this topic composes from ─────────────────────────────────────
  templateSets: readonly TemplateSet[];
  selectedTemplateSet: TemplateSet | null;
  /** The impression-goal sentence, for surfaces that show it outside a body. */
  goalProse: string;
  getTemplateForIntent: (intent: MessageIntent) => Template | null;
  /**
   * The garden form one intent is written from, as its author laid it out, or
   * null on a topic that composes the classic way.
   */
  getFormForIntent: (
    intent: MessageIntent
  ) => readonly SharingFormSlot[] | null;
  /**
   * Every slot of the topic's garden form, across all its intents, carrying
   * the sender's answer and keyed by slot id. A config never reuses an id, so
   * one record covers the whole session. Empty on a classic topic.
   */
  slotsById: Readonly<Record<string, AnsweredSlot>>;
  /** How many people one intent's message reaches, as a slot reads it. */
  addressingModeForIntent: (intent: MessageIntent) => AddressingMode;
  /**
   * The allowed detail this sender's leg carries for one intent's message: the
   * sender's answer to the slot the topic's Settings tab names, or — on a
   * topic that never saved the tab — the `allowedDetail` variable a garden
   * form's slots assign, or the classic template's allowed detail field. ''
   * when the message asks for none.
   */
  allowedDetailForIntent: (intent: MessageIntent) => string;
  /**
   * The field id of the slot in one intent's form the allowed detail comes
   * from, or null when it has none. Only an intro carries an allowed detail,
   * and that slot may be left blank.
   */
  allowedDetailSlotIdForIntent: (intent: MessageIntent) => string | null;

  // ── what it composes ──────────────────────────────────────────────────────
  /**
   * The recipient environment of one garden intent's message to all its
   * recipients: the session's share frame, the intent frame holding the
   * answered form, and the recipients' frame (see `Environment`).
   */
  getEnvironmentForIntent: (intent: MessageIntent) => Environment<FormFacts>;
  /**
   * The same for the message to one recipient, with their own details marked
   * (`PreviewLogic.markPerRecipient`).
   */
  getMarkedEnvironmentForRecipient: (
    intent: MessageIntent,
    recipient: Contact
  ) => Environment<FormFacts>;
  // GARDEN-CUTOVER(#658): classic templates read their primitives from a map.
  getContextForIntent: (intent: MessageIntent) => ContextVars;
  getContextForContact: (contact: Contact) => ContextVars;
  // END GARDEN-CUTOVER(#658)
  getPreviewForIntent: (intent: MessageIntent) => string;
  /**
   * The message `recipient` will receive, cut into pieces at their own
   * details (see ShareWizardContext for an example).
   */
  getPreviewSegmentsForRecipient: (
    intent: MessageIntent,
    recipient: Contact
  ) => readonly PreviewSegment[];
  /**
   * The message `degree` degrees removed (one unless given): what the person
   * that far along would send, if everyone before them passed it on.
   */
  getHypotheticalPreviewForIntent: (
    intent: MessageIntent,
    degree?: number
  ) => string;
  /** Pick new made-up people for the next look at the hypothetical preview. */
  cycleHypotheticalPeople: () => void;
  /** Whether every field the message asks for has been answered. */
  isTemplateHydrated: (intent: MessageIntent) => boolean;
  /**
   * Whether the slot `slotId` names is soft-linked to one the
   * sender is asked in an earlier message of this share, so it is left off
   * the form and prints that slot's answer instead.
   */
  isAnsweredByLinkedSlot: (slotId: string) => boolean;
  /**
   * Store the sender's answer to the slot `slotId` names, and copy it to
   * every slot linked to it that they are not asked on its own (see
   * `ComposeLogic.answerLinked`).
   */
  answerSlot: (slotId: string, answer: string) => void;

  // ── what the send machine asks for ────────────────────────────────────────
  /**
   * One intent's message as `contacts` receive it, or '' when there is
   * nothing to compose it from. With a `legId`, the message's `_topicUrl`
   * primitive renders as that leg's own page, `/b/{legId}`, so opening it
   * credits the leg; without one, as the topic's page, `/topics/{topicId}`.
   */
  composeForIntent: (
    intent: MessageIntent,
    contacts: readonly Contact[],
    legId?: string
  ) => string;
}

/**
 * The message being written: which form this topic composes from, what the
 * primitives in it resolve to, and what the result looks like — both as it will
 * be sent and as it would read one degree removed.
 *
 * The form is resolved before anything else, because which dialect a topic
 * speaks decides what every step below it works with. Everything after that
 * is one of two questions asked repeatedly: what does this message say, and
 * is it finished.
 */
export function useShareComposer({
  topic,
  features,
  sharingMode,
  isUpdateFlow,
  updateSummary,
  previousLegId,
  reshareInfoForPrevLeg,
  userName,
  allowedName,
  connectionToPrevLeg,
  preferredPronouns,
  contactsByIntent,
  intentReachesOnePerson,
  selectedToneId,
  setFieldValues,
  fieldValues,
  presentToast,
}: UseShareComposerParams): ShareComposer {
  // A component-driven (topic_garden) topic stores its customTemplates in the
  // inline share-form dialect; the legacy path stores the classic format.
  const isGardenTopic = useMemo(
    () => !!topic && topicGardenEnabled(features),
    [topic, features]
  );

  /**
   * The inline sharing form in effect: a garden topic's saved form when it
   * still parses — even one whose author removed a slot — else the type's
   * default share form. The SharingSetupModal seeds its editor by the same
   * rule, so what a manager sees there is what the wizard reads here.
   */
  const shareFormYaml = useMemo(() => {
    const ct = topic?.customTemplates;
    return isGardenTopic && ct && FormLogic.isSharingConfig(ct, topic?.type)
      ? ct
      : defaultShareFormYaml(topic?.type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic?.type, topic?.customTemplates, isGardenTopic]);

  /**
   * The garden form a garden topic's messages are composed from. A classic
   * topic has none, and neither does a garden topic whose type has no form that
   * parses; either one composes from the classic templates below.
   */
  const shareFormConfig = useMemo(() => {
    if (!isGardenTopic) return null;
    try {
      return FormLogic.parseSharingConfigRaw(shareFormYaml, topic?.type);
    } catch {
      return null;
    }
  }, [isGardenTopic, shareFormYaml, topic?.type]);

  // The classic templates, for a topic that composes the classic way. A garden
  // topic with a form composes from the form itself and has none.
  const templateSets = useMemo(() => {
    if (shareFormConfig) return [];
    if (!isGardenTopic && topic?.customTemplates) {
      // A manager-authored override (the full classic templates YAML) wins.
      try {
        return TemplateLogic.parseTemplates(topic.customTemplates);
      } catch {
        // fall through to default templates (defensive — saving validates)
      }
    }
    return TemplateLogic.getTemplateSetsForTopicType(topic?.type ?? 'event');
  }, [topic?.type, topic?.customTemplates, isGardenTopic, shareFormConfig]);

  // A single-tone file offers nothing to choose, so it IS the selection.
  const selectedTemplateSet = useMemo(
    () =>
      templateSets.find(t => t.id === selectedToneId) ??
      (templateSets.length === 1 ? templateSets[0] : null),
    [templateSets, selectedToneId]
  );

  // The impression-goal prose (#447), for surfaces that show the sentence
  // outside a composed message body (the future-child preview). Read from the
  // same form the message composes from, so the preview never promises a
  // sentence the message lacks. Any topic type may carry a goal slot; news is
  // just the only type whose shipped default does.
  const goalProse = useMemo(
    () => FormLogic.goalProse(shareFormYaml),
    [shareFormYaml]
  );

  // A stored customTemplates that no longer parses (in the dialect this topic
  // saves in) is corrupt. The memo above already reverted rendering to the
  // default template; here we surface it to the sharer with a toast.
  const customTemplatesCorrupted = useMemo(() => {
    const ct = topic?.customTemplates;
    if (!ct) return false;
    try {
      if (isGardenTopic) FormLogic.parseSharingConfigRaw(ct, topic?.type);
      else TemplateLogic.parseTemplates(ct);
      return false;
    } catch {
      return true;
    }
  }, [topic?.customTemplates, topic?.type, isGardenTopic]);

  // Toast once per topic; a re-open or a fixed template resets it.
  const corruptToastTopicRef = useRef<string | null>(null);
  useEffect(() => {
    if (!customTemplatesCorrupted) {
      if (!topic?.customTemplates) corruptToastTopicRef.current = null;
      return;
    }
    const id = topic?.id ?? null;
    if (!id || corruptToastTopicRef.current === id) return;
    corruptToastTopicRef.current = id;
    presentToast({
      message:
        'This topic’s custom message template is corrupted. Using the default template.',
      duration: 5000,
      color: 'warning',
    });
  }, [
    customTemplatesCorrupted,
    topic?.id,
    topic?.customTemplates,
    presentToast,
  ]);

  const getTemplateForIntent = useCallback(
    (intent: MessageIntent): Template | null => {
      if (!selectedTemplateSet) return null;
      return TemplateLogic.getTemplate(selectedTemplateSet, intent);
    },
    [selectedTemplateSet]
  );

  const getFormForIntent = useCallback(
    (intent: MessageIntent): readonly SharingFormSlot[] | null =>
      shareFormConfig ? ComposeLogic.formForIntent(shareFormConfig, intent) : null,
    [shareFormConfig]
  );

  const slotsById = useMemo(
    (): Readonly<Record<string, AnsweredSlot>> =>
      Object.fromEntries(
        (shareFormConfig?.forms ?? []).flatMap(({ form }) =>
          ComposeLogic.answerForm(form, fieldValues).map(slot => [
            slot.id,
            slot,
          ])
        )
      ),
    [shareFormConfig, fieldValues]
  );

  /** How many people one intent's message reaches, as a slot gate reads it. */
  const addressingModeForIntent = useCallback(
    (intent: MessageIntent): AddressingMode =>
      intentReachesOnePerson(intent) ? 'dm' : 'group-text',
    [intentReachesOnePerson]
  );

  /**
   * For each soft-linked slot in this share, the linked slot the sender is
   * asked in its place (see `ComposeLogic.linkedQuestions`).
   */
  const linkedQuestions = useMemo(
    (): AskedSlotIdByLinkedSlotId =>
      shareFormConfig
        ? ComposeLogic.linkedQuestions(
            shareFormConfig,
            ShareWizardLogic.intentsWithRecipients(contactsByIntent).map(
              intent => ({
                intent,
                addressingMode: addressingModeForIntent(intent),
              })
            ),
            sharingMode
          )
        : new Map(),
    [shareFormConfig, contactsByIntent, addressingModeForIntent, sharingMode]
  );
  const isAnsweredByLinkedSlot = useCallback(
    (slotId: string): boolean => {
      const asked = linkedQuestions.get(slotId);
      return asked !== undefined && asked !== slotId;
    },
    [linkedQuestions]
  );
  // Answers stored before the share knew which slot is asked reach the
  // linked slots left off the form here.
  useEffect(() => {
    setFieldValues(prev => {
      const next = ComposeLogic.fillLinkedAnswers(linkedQuestions, prev);
      return next === prev ? prev : { ...next };
    });
  }, [linkedQuestions, fieldValues, setFieldValues]);
  const answerSlot = useCallback(
    (slotId: string, answer: string) =>
      setFieldValues(prev =>
        shareFormConfig
          ? {
              ...ComposeLogic.answerLinked(
                shareFormConfig,
                linkedQuestions,
                prev,
                slotId,
                answer
              ),
            }
          : { ...prev, [slotId]: answer }
      ),
    [setFieldValues, shareFormConfig, linkedQuestions]
  );

  /**
   * The session's share frame: the topic, now, the sender and their update,
   * and in a reshare the run's reach and pronouns from the previous leg (#447).
   * Until that leg's payload loads, those primitives render empty (the step
   * gates sending on it).
   */
  const shareFacts = useMemo((): ShareFacts | null => {
    if (!topic) return null;
    const facts: ShareFacts = {
      topic,
      now: new Date(),
      // The f= sender name the leg link carries (via _topicUrl). Every flow
      // reads the profile's familiar name, news included: the share guard
      // requires one before any of them can reach this wizard. A hypothetical
      // message is the exception and passes a made-up name.
      profileName: userName ?? '',
      isUpdateFlow,
      updateSummary: isUpdateFlow ? updateSummary : undefined,
    };
    if (sharingMode !== 'reshare' || !reshareInfoForPrevLeg) return facts;
    return {
      ...facts,
      ...ReshareStoryLogic.runFacts(
        reshareInfoForPrevLeg.runImpressionCount,
        reshareInfoForPrevLeg.startingSenderPronouns
      ),
      impressionGoalMax: features.impressionGoalMax,
    };
  }, [
    topic,
    userName,
    isUpdateFlow,
    updateSummary,
    sharingMode,
    reshareInfoForPrevLeg,
    features.impressionGoalMax,
  ]);

  const shareEnv = useMemo(
    () => (shareFacts ? EnvironmentLogic.shareEnvironment(shareFacts) : null),
    [shareFacts]
  );

  /**
   * One intent's frame over `share`: the reshare's `story` facts, and the
   * form the intent composes from carrying `answers`. A classic template has
   * no form, so its intent frame holds an empty one.
   */
  const intentEnvironment = useCallback(
    (
      share: Environment<ShareFacts>,
      intent: MessageIntent,
      story: IntentFacts,
      answers: Record<string, string> = fieldValues,
      mode: SharingMode = sharingMode
    ): Environment<ShareFacts & IntentFacts & FormFacts> => {
      const form = getFormForIntent(intent);
      return EnvironmentLogic.extendWithDefinitions(
        share,
        {
          ...story,
          form: form ? ComposeLogic.answerForm(form, answers) : [],
          sharingMode: mode,
        },
        INTENT_DEFINITIONS
      );
    },
    [fieldValues, getFormForIntent, sharingMode]
  );

  /**
   * The sender's allowed detail for one intent's message, resolved by name
   * after the form's last slot. A garden form's slots hand the detail over by
   * assigning variables, so only a slot this sharing mode asks can supply it:
   * an answer left over from introducing the topic never rides along on a
   * reshare. A topic that has saved its Settings tab passes on the answer to
   * the slot it names, by that slot's id, or nothing when it names none
   * (see `ComposeLogic.allowedDetail`); one that never has passes on whatever
   * the form's slots assign to the `allowedDetail` variable.
   *
   * The reshare story is told from the allowed detail, so the detail resolves
   * in an intent frame with no story yet (validation rejects an allowed
   * detail that reads it), and with no recipients, since one leg carries it
   * to all of them. A classic template collects the detail in the field
   * `ALLOWED_DETAIL_FIELD_ID` instead.
   */
  const allowedDetailSettings =
    topic?.reshareStorySettings?.allowedDetailSettings;
  const allowedDetailSlotIdForIntent = useCallback(
    (intent: MessageIntent): string | null =>
      TopicLogic.allowedDetailSlotId(allowedDetailSettings, intent),
    [allowedDetailSettings]
  );
  const allowedDetailForIntent = useCallback(
    (intent: MessageIntent): string => {
      if (!shareEnv) return '';
      const recipientsEnv = EnvironmentLogic.extendWithDefinitions(
        intentEnvironment(shareEnv, intent, { connectionToPrevLeg }),
        { contacts: [] },
        RECIPIENT_DEFINITIONS
      );
      if (getFormForIntent(intent)) {
        const evaluated = ComposeLogic.evaluateForm(recipientsEnv);
        return topic?.reshareStorySettings
          ? ComposeLogic.allowedDetail(allowedDetailSettings, id => {
              const slot = slotsById[id];
              const env = evaluated.slotEnvironments.get(id);
              return slot && env ? SlotLogic.evaluate(slot, env) : null;
            })
          : (
              EnvironmentLogic.resolve(
                evaluated.resultingEnvironment,
                ALLOWED_DETAIL_VARIABLE
              ) ?? ''
            ).trim();
      }
      // GARDEN-CUTOVER(#658): classic templates only. Delete this region when
      // every topic composes from a garden sharing form. A classic template
      // has no variables of its own, so the detail is the field
      // `ALLOWED_DETAIL_FIELD_ID`, read by id.
      const template = getTemplateForIntent(intent);
      if (!template) return '';
      const resolve = TemplateLogic.resolver(
        template,
        fieldValues,
        TemplateLogic.contextOf(recipientsEnv)
      );
      return (resolve(ALLOWED_DETAIL_FIELD_ID) ?? '').trim();
      // END GARDEN-CUTOVER(#658)
    },
    [
      connectionToPrevLeg,
      intentEnvironment,
      fieldValues,
      getFormForIntent,
      getTemplateForIntent,
      shareEnv,
      slotsById,
      topic?.reshareStorySettings,
      allowedDetailSettings,
    ]
  );

  // This sender's own leg as the story will tell it once sent: the name they
  // let travel, the connection they gave (one answer for the whole session,
  // whichever intents have recipients), and their allowed detail. Everything
  // the server needs to render this leg and any made-up leg that passes it on.
  const ownLeg = useMemo(
    (): LegStoryInfoLike => ({
      allowedSenderName: allowedName,
      connectionToPrevLeg: connectionToPrevLeg.trim(),
      allowedDetail: allowedDetailForIntent('intro'),
      // The story refers back to this sender by these, as it will once the
      // leg is sent.
      senderPronouns: preferredPronouns,
    }),
    [
      allowedName,
      connectionToPrevLeg,
      allowedDetailForIntent,
      preferredPronouns,
    ]
  );
  // Server-rendered stories, like "I reshared this from Bob, who I know. He's
  // 2 degrees separated from Jen, who got the ball rolling." The story is
  // grammar, and grammar lives on the server: the frontend only says which
  // legs it is told from.
  const storyTeller = useReshareStories();

  // The story of this sender's leg and any made-up ones after it, oldest
  // first. A reshare reads on from the real leg it passes on, whose topic's
  // settings the server applies; a first share starts a run of its own, told
  // by this topic's settings.
  const tellStory = useCallback(
    (futureLegsNearToFar: LegStoryInfoLike[]): string | null =>
      sharingMode === 'reshare' && previousLegId
        ? storyTeller.story({ futureLegsNearToFar, previousLegId })
        : storyTeller.fictionalStory({
            futureLegsNearToFar,
            sharingSettings: topic
              ? TopicLogic.storySharingSettings(topic)
              : {},
          }),
    [sharingMode, previousLegId, storyTeller, topic]
  );

  // The `{_reshareStory}` of this sender's own message: their story from their
  // `{_connectionToPrevLeg}` on, rendered on top of the previous leg's stored
  // history.
  // Null until it lands, which `isTemplateHydrated` reads as not yet ready.
  const ownStory = useMemo(
    () => (sharingMode === 'reshare' ? tellStory([ownLeg]) : null),
    [sharingMode, tellStory, ownLeg]
  );

  /**
   * The intent frame's reshare facts: the connection and the story, set only
   * once the previous leg's payload loads, like the reach and pronouns.
   */
  const storyFacts = useMemo(
    (): IntentFacts =>
      sharingMode === 'reshare' && reshareInfoForPrevLeg
        ? {
            connectionToPrevLeg: ownLeg.connectionToPrevLeg,
            reshareStory: ownStory ?? '',
          }
        : {},
    [sharingMode, reshareInfoForPrevLeg, ownLeg.connectionToPrevLeg, ownStory]
  );

  /**
   * The recipient environment of one intent's message to `contacts`, sent on
   * `legId` when given, under the session's share frame and the intent's.
   */
  const recipientEnvironment = useCallback(
    (
      intent: MessageIntent,
      contacts: readonly Contact[],
      legId?: string,
      definitions: readonly PrimitiveDefinition<
        ShareFacts & IntentFacts & RecipientFacts
      >[] = RECIPIENT_DEFINITIONS
    ): Environment<ShareFacts & IntentFacts & FormFacts> | null => {
      return shareEnv
        ? EnvironmentLogic.extendWithDefinitions(
            intentEnvironment(shareEnv, intent, storyFacts),
            { contacts, legId },
            definitions
          )
        : null;
    },
    [intentEnvironment, shareEnv, storyFacts]
  );

  /**
   * One intent's message composed in `recipientsEnv`, or '' when there is
   * nothing to compose it from. A garden topic composes by visiting its form,
   * asking each slot what it contributes; the classic path still composes from
   * a template body, which knows nothing of sharing modes.
   */
  const composeIn = useCallback(
    (
      intent: MessageIntent,
      recipientsEnv: Environment<FormFacts> | null,
      answers: Record<string, string> = fieldValues
    ): string => {
      if (!recipientsEnv) return '';
      if (getFormForIntent(intent))
        return ComposeLogic.composeBody(recipientsEnv);
      // GARDEN-CUTOVER(#658): classic templates only.
      const template = getTemplateForIntent(intent);
      if (!template) return '';
      return TemplateLogic.composeBody(
        template,
        answers,
        TemplateLogic.contextOf(recipientsEnv)
      );
      // END GARDEN-CUTOVER(#658)
    },
    [fieldValues, getFormForIntent, getTemplateForIntent]
  );

  const getEnvironmentForIntent = useCallback(
    (intent: MessageIntent): Environment<FormFacts> =>
      recipientEnvironment(intent, contactsByIntent[intent]) ??
      ComposeLogic.formEnvironment([]),
    [contactsByIntent, recipientEnvironment]
  );

  const getMarkedEnvironmentForRecipient = useCallback(
    (intent: MessageIntent, recipient: Contact): Environment<FormFacts> =>
      recipientEnvironment(
        intent,
        [recipient],
        undefined,
        PreviewLogic.markPerRecipient(RECIPIENT_DEFINITIONS)
      ) ?? ComposeLogic.formEnvironment([]),
    [recipientEnvironment]
  );

  // GARDEN-CUTOVER(#658): a classic template reads its primitives from a map.
  // Its intent frame holds no form, so either intent's will do.
  const getContextForIntent = useCallback(
    (intent: MessageIntent): ContextVars =>
      TemplateLogic.contextOf(
        recipientEnvironment(intent, contactsByIntent[intent]) ??
          EnvironmentLogic.theEmptyEnvironment
      ),
    [contactsByIntent, recipientEnvironment]
  );

  const getContextForContact = useCallback(
    (contact: Contact): ContextVars =>
      TemplateLogic.contextOf(
        recipientEnvironment('intro', [contact]) ??
          EnvironmentLogic.theEmptyEnvironment
      ),
    [recipientEnvironment]
  );
  // END GARDEN-CUTOVER(#658)

  const composeForIntent = useCallback(
    (
      intent: MessageIntent,
      contacts: readonly Contact[],
      legId?: string
    ): string =>
      composeIn(intent, recipientEnvironment(intent, contacts, legId)),
    [composeIn, recipientEnvironment]
  );

  const getPreviewForIntent = useCallback(
    (intent: MessageIntent): string =>
      composeForIntent(intent, contactsByIntent[intent]),
    [composeForIntent, contactsByIntent]
  );

  // The per-recipient values go in bracketed, so the composed text still shows
  // which parts of it are the recipient's own details.
  const getPreviewSegmentsForRecipient = useCallback(
    (intent: MessageIntent, recipient: Contact): readonly PreviewSegment[] =>
      PreviewLogic.segments(
        composeIn(
          intent,
          recipientEnvironment(
            intent,
            [recipient],
            undefined,
            PreviewLogic.markPerRecipient(RECIPIENT_DEFINITIONS)
          )
        )
      ),
    [composeIn, recipientEnvironment]
  );

  /**
   * The message one intent's next sender would send, `degree` degrees removed,
   * to the made-up `people`: what the primitives would resolve to if everyone
   * before them passed this on. Three things move when a message travels,
   * and all three move here:
   *
   * - it is signed by whoever passes it on, as made up as the person it
   *   reaches, and they answer for themselves wherever the form asks who is
   *   sending;
   * - the story gains a leg per degree. This sender becomes the previous leg
   *   of the next one, and each made-up resharer before that contributes to a
   *   reverse chronological history of legs; the last person's connection to
   *   whoever passed it to them is unknowable, so a plain one takes its place.
   *   The server tells the whole story from that chain of legs, on top of the
   *   real history this sender passes on, so it reads exactly as a real one
   *   would — empty until the rendering lands.
   * - the reach grows by this send, and by as much again per made-up
   *   resharer. The run's count already includes everyone upstream.
   *
   * The next leg is a reshare by definition — someone passing this message
   * on — so it composes from the reshare body even when this send is a first
   * share.
   */
  const composeNextDegree = useCallback(
    (
      intent: MessageIntent,
      people: HypotheticalPeople,
      degree: number
    ): string => {
      if (!shareFacts) return '';
      const share = EnvironmentLogic.shareEnvironment({
        ...shareFacts,
        profileName: people.senderName,
        ...ReshareStoryLogic.runFacts(
          PreviewLogic.hypotheticalReach(
            reshareInfoForPrevLeg?.runImpressionCount ?? 0,
            contactsByIntent[intent].length,
            degree
          ),
          // Whose race it is does not change as it travels: the person who
          // started the topic is still the subject of the goal sentence.
          reshareInfoForPrevLeg?.startingSenderPronouns ?? preferredPronouns
        ),
        impressionGoalMax: features.impressionGoalMax,
      });
      const form = getFormForIntent(intent);
      const answers = form
        ? PreviewLogic.answersOfTheNextSender(form, fieldValues)
        : fieldValues;
      const story: IntentFacts = {
        // The next person's connection to this sender is unknowable, so a
        // made-up one stands in for it, matching the story's own made-up legs.
        connectionToPrevLeg: people.senderConnection,
        reshareStory: tellStory(PreviewLogic.storyLegs(people, ownLeg)) ?? '',
      };
      return composeIn(
        intent,
        EnvironmentLogic.extendWithDefinitions(
          intentEnvironment(share, intent, story, answers, 'reshare'),
          { contacts: [people.recipient] },
          RECIPIENT_DEFINITIONS
        ),
        answers
      );
    },
    [
      composeIn,
      contactsByIntent,
      intentEnvironment,
      features.impressionGoalMax,
      fieldValues,
      getFormForIntent,
      shareFacts,
      ownLeg,
      preferredPronouns,
      reshareInfoForPrevLeg,
      tellStory,
    ]
  );

  // Which made-up people the preview currently names. The sheet advances
  // it as it closes, so a look is never interrupted by the names
  // changing under it and the next one brings a new pair.
  const [hypotheticalPeopleIndex, setHypotheticalPeopleIndex] = useState(0);
  const cycleHypotheticalPeople = useCallback(
    () => setHypotheticalPeopleIndex(i => i + 1),
    []
  );

  const getHypotheticalPreviewForIntent = useCallback(
    (intent: MessageIntent, degree = 1): string =>
      PreviewLogic.hypotheticalPreview({
        compose:
          getFormForIntent(intent) || getTemplateForIntent(intent)
            ? people => composeNextDegree(intent, people, degree)
            : null,
        peopleIndex: hypotheticalPeopleIndex,
        degree,
      }),
    [
      composeNextDegree,
      getFormForIntent,
      getTemplateForIntent,
      hypotheticalPeopleIndex,
    ]
  );

  const isTemplateHydrated = useCallback(
    (intent: MessageIntent): boolean => {
      // A reshare's story is part of its message, and it comes from the
      // server: until it lands the message is not finished.
      if (sharingMode === 'reshare' && ownStory === null) return false;
      const recipientsEnv = recipientEnvironment(
        intent,
        contactsByIntent[intent]
      );
      if (!recipientsEnv) return false;
      const form = getFormForIntent(intent);
      if (form) {
        // The connection the story opens with is asked once for the session,
        // outside any intent's own questions, so the form cannot see it.
        if (
          sharingMode === 'reshare' &&
          ComposeLogic.contributesReshareStory(form) &&
          connectionToPrevLeg.trim() === ''
        ) {
          return false;
        }
        return ComposeLogic.isComplete(
          recipientsEnv,
          addressingModeForIntent(intent),
          allowedDetailSlotIdForIntent(intent)
        );
      }
      // GARDEN-CUTOVER(#658): classic templates only.
      const template = getTemplateForIntent(intent);
      if (!template) return false;
      return TemplateLogic.isComplete(
        template,
        fieldValues,
        TemplateLogic.contextOf(recipientsEnv),
        intentReachesOnePerson(intent)
      );
      // END GARDEN-CUTOVER(#658)
    },
    [
      addressingModeForIntent,
      connectionToPrevLeg,
      contactsByIntent,
      fieldValues,
      getFormForIntent,
      getTemplateForIntent,
      intentReachesOnePerson,
      recipientEnvironment,
      allowedDetailSlotIdForIntent,
      sharingMode,
      ownStory,
    ]
  );

  // Drop any stored field value that the current mode no longer offers — e.g. a
  // singleton-only greeting picked while texting individually, then the user
  // switched that intent to a group text. Custom "Other" text is preserved.
  useEffect(() => {
    setFieldValues(prev =>
      ShareWizardLogic.pruneStaleFieldValues(prev, (fieldId, value) =>
        // Field ids are unique across every intent, so at most one intent's
        // form or template declares this one.
        MESSAGE_INTENTS.some(intent => {
          const form = getFormForIntent(intent);
          if (form) {
            return ComposeLogic.isStaleAnswer(
              form,
              fieldId,
              value,
              addressingModeForIntent(intent)
            );
          }
          const field = getTemplateForIntent(intent)?.fields.find(
            f => f.id === fieldId
          );
          return (
            !!field &&
            TemplateLogic.isStaleOptionValue(
              field,
              value,
              intentReachesOnePerson(intent)
            )
          );
        })
      )
    );
  }, [
    addressingModeForIntent,
    getFormForIntent,
    getTemplateForIntent,
    intentReachesOnePerson,
    setFieldValues,
  ]);

  return {
    templateSets,
    selectedTemplateSet,
    goalProse,
    getTemplateForIntent,
    getFormForIntent,
    slotsById,
    addressingModeForIntent,
    allowedDetailForIntent,
    allowedDetailSlotIdForIntent,
    getEnvironmentForIntent,
    getMarkedEnvironmentForRecipient,
    getContextForIntent,
    getContextForContact,
    getPreviewForIntent,
    getPreviewSegmentsForRecipient,
    getHypotheticalPreviewForIntent,
    cycleHypotheticalPeople,
    isTemplateHydrated,
    isAnsweredByLinkedSlot,
    answerSlot,
    composeForIntent,
  };
}
